import { useGetWalletQuery, useTopUpMutation } from '@/models/wallet';
import { useForm } from 'react-hook-form';
import { getStripe } from '@/features/payments';
import { Trans, useTranslation } from 'react-i18next';
import { Card } from '@/features/card';
import { Button } from '@/components/Button';
import { Typography } from '@mui/material';
import { FormInput } from '@/components/FormInput';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useProfileQuery } from '@/models/user';

type FormProps = {
  amount: number;
};

export const WalletPage = () => {
  const { data } = useGetWalletQuery();
  const { t } = useTranslation();
  const [triggerTopUp] = useTopUpMutation();
  const { data: user } = useProfileQuery();

  const value = data ? parseFloat(data?.balance).toFixed(2) : 0;

  const schema = z.object({
    amount: z
      .string()
      .or(z.number())
      .transform((val) => (typeof val === 'string' ? Number(val) : val))
      .refine((val) => val >= 500, {
        message: t('page.wallet.input.amount'),
      }),
  });

  const {
    control,
    trigger,
    formState: { errors },
    handleSubmit,
  } = useForm<FormProps>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      amount: 500,
    },
  });

  const handleTopUp = async (data: FormProps) => {
    const validated = await trigger();

    if (validated) {
      await initiateTopUp(Number(data.amount));
    }
  };

  const initiateTopUp = async (amount: number) => {
    const stripe = await getStripe();

    // TODO: why is it needed here?
    if (user?.id) {
      const response = await triggerTopUp({
        productTitle: t('message.wallet.topUp.productTitle'),
        productDescription: t('message.wallet.topUp.productDescription'),
        amount,
      }).unwrap();

      const { error } = await stripe.redirectToCheckout({
        sessionId: response.result,
      });

      console.warn(error.message);
    }
  };

  return (
    <div className="flex flex-col w-full">
      <div className="mb-10">
        <Typography variant="h4" fontWeight={500}>
          Your balance
        </Typography>
      </div>
      <div className="flex gap-4 mb-4">
        <div className="flex basis-1/4">
          <Card>
            <div className="rounded-2xl">
              <Trans i18nKey="page.wallet.current_balance" />
              <div className="mb-4">
                <span className="mr-1 text-3xl font-bold">{value}</span>
                <span className="font-bold">
                  <Trans i18nKey="common.label.pln" />
                </span>
              </div>
            </div>
          </Card>
        </div>
        <div className="flex basis-3/4">
          <Card>
            <Trans i18nKey="page.wallet.message" />
          </Card>
        </div>
      </div>
      <div>
        <Card>
          <div className="flex justify-between gap-4">
            <div className="basis-1/4">
              <Typography variant="h6" fontWeight={500}>
                Stripe
              </Typography>
              <Typography variant="body1" mb={2}>
                Visa/MasterCard, Google Pay, Blik
              </Typography>
              <form>
                <FormInput
                  name="amount"
                  control={control}
                  error={!!errors.amount}
                  fullWidth
                  // helperText={errors[field.name] && errors[field.name].message}
                />
                <Button
                  variant="contained"
                  fullWidth
                  type="submit"
                  onClick={handleSubmit(handleTopUp)}
                >
                  <Trans i18nKey="page.wallet.top_up_wallet" />
                </Button>
              </form>
            </div>
            <div className="basis-3/4">
              <Typography variant="h6" fontWeight={500}>
                <Trans i18nKey="page.wallet.bonus.title" />
              </Typography>
              <Typography variant="body1" mb={2}>
                <Trans i18nKey="page.wallet.bonus.subtitle" />
              </Typography>
              <div></div>
              <div className="flex gap-4">
                <Card className="bg-linear-to-br from-emerald-50 to-emerald-300">
                  <div className="text-emerald-800">
                    <Trans i18nKey="page.wallet.bonus.card.top_up" />
                    <div className="mb-2 font-bold">
                      1000-1999 <Trans i18nKey="common.label.pln" />
                    </div>
                    <Trans i18nKey="page.wallet.bonus.card.receive" />
                    <Typography variant="h4" fontWeight={500}>
                      100 <Trans i18nKey="common.label.pln" />
                    </Typography>
                  </div>
                  <div
                    className="absolute z-0 font-bold -rotate-45 text-8xl left-1/2 bottom-1/2 opacity-10 text-emerald-600"
                    style={{ width: '300px' }}
                  >
                    10%
                  </div>
                </Card>
                <Card className="bg-linear-to-br from-amber-50 to-amber-300">
                  <div className="text-amber-800">
                    <Trans i18nKey="page.wallet.bonus.card.top_up" />
                    <div className="mb-2 font-bold">
                      2000-2999 <Trans i18nKey="common.label.pln" />
                    </div>
                    <Trans i18nKey="page.wallet.bonus.card.receive" />
                    <Typography variant="h4" fontWeight={500}>
                      300 <Trans i18nKey="common.label.pln" />
                    </Typography>
                  </div>
                  <div
                    className="absolute z-0 font-bold -rotate-45 text-8xl left-1/2 bottom-1/2 opacity-10 text-amber-600"
                    style={{ width: '300px' }}
                  >
                    15%
                  </div>
                </Card>
                <Card className="bg-linear-to-br from-pink-50 to-pink-300">
                  <div className="text-pink-800">
                    <Trans i18nKey="page.wallet.bonus.card.top_up" />
                    <div className="mb-2 font-bold">
                      5000+ <Trans i18nKey="common.label.pln" />
                    </div>
                    <Trans i18nKey="page.wallet.bonus.card.receive" />
                    <Typography variant="h4" fontWeight={500}>
                      1500 <Trans i18nKey="common.label.pln" />
                    </Typography>
                  </div>
                  <div
                    className="absolute z-0 font-bold text-pink-600 -rotate-45 text-8xl left-1/2 bottom-1/2 opacity-10"
                    style={{ width: '300px' }}
                  >
                    30%
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
